/***
 * Fast Search - Lightweight React based replacement for the default WordPress search
 */

const { useState, useEffect, createRoot } = wp.element;

import ResultsCard from "./resultsCard";

import DOMReady from "./util/dom-ready";
import ReactPaginate from "react-paginate";

const FastSearch = () => {
	// Never allow searching more than these post types passed in from the server

	const postTypes = window.fastSearchPostTypes || [];
	const tags = window.fastSearchTags || [];
	const excludeTags = window.fastSearchTagsExclude || [];
	const audience = window.fastSearchAudience || {};
	const excludeAudience = window.fastSearchAudienceExclude || {};

	// Allow filtering by these filters and these options passed in from the server
	const filters = window.fastSearchFilters || [];

	const params = new Proxy(new URLSearchParams(window.location.search), {
		get: (searchParams, prop) => searchParams.get(prop),
	});

	const getFiltersFromParams = () => {
		let urlFilters = {};
		Object.keys(filters).forEach((key) => {
			// let filter = filters[key];
			if (params[key]) {
				urlFilters[key] = {};
				params[key].split(",").forEach((option) => {
					urlFilters[key][option] = true;
				});
			}
		});
		return urlFilters;
	};

	const [searchStr, setSearchStr] = useState(params.search || "");
	// TODO: Filters and post types are being managed differently
	const [checkedFilters, setCheckedFilters] = useState(
		getFiltersFromParams() || {}
	);
	const [searchRes, setSearchRes] = useState();
	const [totalPages, setTotalPages] = useState(0);
	const [currentPage, setCurrentPage] = useState(params["results_page"] || 1);
	const [totalResults, setTotalResults] = useState(0);
	const [sort, setSort] = useState(params.sort || "date");
	const [sorted, setSorted] = useState(params.sort || "default");
	const [dropdownOpen, setDropdownOpen] = useState(null);

	const itemsPerPage = 24;

	const pageCount = Math.ceil(totalResults / itemsPerPage);

	const sorts = {
		relevance: "Relevance",
		date: "Date (Newest)",
		"date-asc": "Date (Oldest)",
		title: "Title (A-Z)",
		"title-desc": "Title (Z-A)",
	};

	const searchWithAPI = (query, activeFilters, sort, currentPage) => {
		if (!query) {
			// TODO: We might want to always search
			query = "";
		}

		const url = new URL(location);

		url.searchParams.set("search", query);
		url.searchParams.set("results_page", currentPage);
		url.searchParams.set("sort", sort);

		Object.keys(filters).forEach((key) => {
			if (
				activeFilters.hasOwnProperty(key) &&
				Object.keys(activeFilters[key]).length > 0
			) {
				url.searchParams.set(key, Object.keys(activeFilters[key]).join(","));
			} else {
				url.searchParams.delete(key);
			}
		});

		url.searchParams.set("post_type_default", postTypes.join(","));

		if (tags.length) {
			url.searchParams.set("tags", tags.join(","));
		}
		if (excludeTags.length) {
			url.searchParams.set("tags_exclude", excludeTags.join(","));
		}
		if (audience.length) {
			url.searchParams.set("audience", audience.join(","));
		}
		if (excludeAudience.length) {
			url.searchParams.set("audience_exclude", excludeAudience.join(","));
		}

		// Use replace state if you don't want to add to the browser history
		history.pushState({}, "", url);

		fetch(`/wp-json/wp/v2/search?${url.searchParams}&page=${currentPage}`)
			.then((res) => {
				setTotalResults(parseInt(res.headers.get("X-WP-Total")));
				setTotalPages(parseInt(res.headers.get("X-WP-Totalpages")));
				let _totalPages = parseInt(res.headers.get("X-WP-Totalpages"));
				setCurrentPage(parseInt(url.searchParams.get("results_page")) || 1);
				return res.json();
			})
			.then((res) => {
				setSearchRes(res);
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const clearSearch = (e) => {
		setSearchStr("");
		setSearchRes([]);
	};

	const handleFilterChange = (e) => {
		checkedFilters[e.target.dataset.filterIndex] =
			checkedFilters[e.target.dataset.filterIndex] || {};
		if (e.target.checked) {
			checkedFilters[e.target.dataset.filterIndex][e.target.value] =
				e.target.checked;
		} else {
			delete checkedFilters[e.target.dataset.filterIndex][e.target.value];
		}

		setCheckedFilters({ ...checkedFilters });
	};

	const handleChange = (e) => {
		e.preventDefault();
		// If the sort has never been set manually, then
		// set it to relevance the first time someone changes
		// the search string
		if (sorted == "default") {
			setSort("relevance");
		}
		setSearchStr(e.target.value);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
	};

	const isChecked = (filterIndex, optionIndex) => {
		return (
			checkedFilters[filterIndex] && checkedFilters[filterIndex][optionIndex]
		);
	};

	const handleSortChange = (e) => {
		setSort(e.target.value);
		setSorted(e.target.value);
	};

	const sortIsChecked = (key) => {
		return key === sort;
	};

	const isDropdownOpen = (type, name) => {
		return dropdownOpen === name;
	};

	const handleDropdownChange = (e) => {
		setDropdownOpen(e.target.checked ? e.target.id : null);
	};

	const handleBodyClick = (e) => {
		let theOpenDropdown = document.querySelector(
			'.dropdown[aria-expanded="true"]'
		);
		if (
			!theOpenDropdown ||
			theOpenDropdown.contains(e.target) ||
			theOpenDropdown == e.target
		) {
			return;
		}
		setDropdownOpen(null);
	};

	// Invoke when user click to request another page.
	const handlePageClick = (event) => {
		const newOffset = event.selected;
		setCurrentPage(newOffset + 1);
	};

	useEffect(() => {
		searchWithAPI(searchStr, checkedFilters, sort, currentPage);
	}, [searchStr, checkedFilters, sort, currentPage]);

	useEffect(() => {
		if (dropdownOpen) {
			document.body.addEventListener("click", handleBodyClick);
		} else {
			document.body.removeEventListener("click", handleBodyClick);
		}
	}, [dropdownOpen]);

	return (
		<div className="fast-search">
			<form onSubmit={handleSubmit} className="search-form">
				<div className="row">
					{/* Filters */}
					<div className="search-filters-container">
						{filters && Object.keys(filters).length > 0 && (
							<div
								className="dropdown dropdown-filters"
								aria-expanded={isDropdownOpen("dropdown", "filters")}
							>
								<input
									type="checkbox"
									className="dropdown-input"
									id="filters"
									checked={isDropdownOpen("dropdown", "filters")}
									onChange={handleDropdownChange}
								/>
								<label className="dropdown-label" htmlFor="filters">
									Filter by{" "}
								</label>
								<div className="dropdown-content">
									<div className="tabs">
										{Object.keys(filters).length > 0 &&
											Object.keys(filters).map((i) => {
												let filter = filters[i];
												return (
													<div className="tab" key={i}>
														<input
															type="checkbox"
															className="tab-input"
															id={filter.name}
														/>
														<label className="tab-label" htmlFor={filter.name}>
															{filter.label}
														</label>
														<div
															className="tab-content"
															data-type={filter.type}
														>
															{Object.keys(filter.options).map((key, i2) => {
																let option = filter.options[key];
																return (
																	<div
																		key={i2}
																		className="checkbox-round-wrapper"
																	>
																		<input
																			type="checkbox"
																			data-filter-index={filter.name}
																			id={option.id}
																			name={filter.name}
																			value={option.id}
																			onChange={handleFilterChange}
																			className="checkbox-round"
																			checked={isChecked(
																				filter.name,
																				option.id
																			)}
																		/>
																		<label
																			className="filter-label checkbox-round-label"
																			htmlFor={option.id}
																		>
																			{option.label}
																		</label>
																	</div>
																);
															})}
														</div>
													</div>
												);
											})}
									</div>
								</div>
							</div>
						)}
						{sorts && Object.keys(sorts).length > 0 && (
							<div
								className="dropdown dropdown-sorts"
								aria-expanded={isDropdownOpen("dropdown", "sorts")}
							>
								<input
									type="checkbox"
									className="dropdown-input"
									id="sorts"
									checked={isDropdownOpen("dropdown", "sorts")}
									onChange={handleDropdownChange}
								/>
								<label className="dropdown-label" htmlFor="sorts">
									Sort by {sorts[sort]}{" "}
								</label>
								<div className="dropdown-content">
									<div className="dropdown-content-wrapper">
										{Object.keys(sorts).map((key, label) => {
											let option = sorts[key];
											return (
												<div key={key} className="checkbox-round-wrapper">
													<input
														type="checkbox"
														id={key}
														name={key}
														value={key}
														onChange={handleSortChange}
														className="checkbox-round"
														checked={sortIsChecked(key)}
													/>
													<label
														className="filter-label checkbox-round-label"
														htmlFor={key}
													>
														{option}
													</label>
												</div>
											);
										})}
									</div>
								</div>
							</div>
						)}
					</div>

					{/* Search box */}
					<div className="search-input-container">
						<input
							type="text"
							value={searchStr}
							onChange={handleChange}
							placeholder="Search..."
						/>
						<button
							type="button"
							onClick={clearSearch}
							className="button--primary"
						>
							Clear
						</button>
					</div>
				</div>
				<div className="row">
					<div className="filters-active">
						{Object.keys(checkedFilters).length > 0 && (
							<div
								className="clear-filters"
								onClick={() => setCheckedFilters({})}
							>
								Clear all filters
							</div>
						)}
						{Object.keys(checkedFilters).map((key, i) => {
							return Object.keys(checkedFilters[key]).map((key2, i2) => {
								let option = filters[key].options[key2];
								return (
									<div key={`${key}-${key2}`}>
										<input
											type="checkbox"
											data-filter-index={key}
											id={option.id}
											value={option.id}
											onChange={handleFilterChange}
											className="checkbox-pill"
											checked={isChecked(key, key2)}
										/>
										<label className="checkbox-pill-label" htmlFor={option.id}>
											<img
												width="12px"
												height="12px"
												src="/wp-content/themes/equity-methods-v2/images/close-x.svg"
											/>{" "}
											{option.label}
										</label>
									</div>
								);
							});
						})}
					</div>

					{searchRes && (
						<div className="search-results-meta">
							{searchRes.length >= itemsPerPage ? (
								<>
									Displaying {itemsPerPage * currentPage - itemsPerPage + 1} to{" "}
									{itemsPerPage * currentPage} of {totalResults} results{" "}
								</>
							) : (
								<>
									Displaying {itemsPerPage * currentPage - itemsPerPage + 1} to{" "}
									{totalResults} of {totalResults} results
								</>
							)}
							<br />
						</div>
					)}
				</div>
			</form>

			<div className="search-results">
				{searchRes && searchRes.length > 0 ? (
					searchRes.map((res, i) => {
						return <ResultsCard res={res} i={i} />;
					})
				) : (
					<div className="no-results">
						<div className="wrapper">
							No Results found, try changing your search terms.
						</div>
					</div>
				)}
			</div>

			<div className="pagination">
				<ReactPaginate
					breakLabel="..."
					nextLabel="next >"
					onPageChange={handlePageClick}
					pageRangeDisplayed={3}
					pageCount={pageCount}
					previousLabel="< previous"
					marginPagesDisplayed={0}
					renderOnZeroPageCount={null}
					forcePage={currentPage - 1}
					hrefBuilder={(currentPage) => {
						const url = new URL(location);
						url.searchParams.set("results_page", currentPage);
						return url.href;
					}}
				/>
			</div>
		</div>
	);
};

DOMReady(() => {
	const fastSearch = document.getElementById("fast-search-root");
	if (fastSearch) {
		createRoot(fastSearch).render(<FastSearch />);
	}
});
